/**
 * A document's file type, derived from its filename extension at upload time.
 * `null`/absent means a legacy row that is treated as a PDF. Mirrors the
 * backend `FileType` literal in `api/app/enums/file_type.py` — keep the two in
 * sync.
 */
export type FileType = "pdf" | "xlsx" | "csv";

/**
 * Minimal document info for source link fallback.
 * Used when opening source links for documents without citations.
 */
export interface IDocumentInfo {
    document_id: number;
    document_name: string;
    download_filename?: string;
    // null for non-PDF documents, which have no inline preview URL; consumers guard it.
    presigned_url: string | null;
    file_type?: FileType | null;
    shared_by_organization_name: string | null;
    shared_group_name: string | null;
}

/** Alternate representations `GET /documents/{id}` presigns instead of the document's own file. */
export const DOCUMENT_INFO_VARIANTS = ["report_internal"] as const;

export type DocumentInfoVariant = (typeof DOCUMENT_INFO_VARIANTS)[number];

export interface IDocumentInfoOptions {
    variant?: DocumentInfoVariant;
}

// ---------------------------------------------------------------------------
// Field Extraction Status
// ---------------------------------------------------------------------------

export type FieldExtractionStatus =
    | "pending"
    | "processing"
    | "complete"
    | "failed"
    | "not_applicable";

export const TERMINAL_EXTRACTION_STATUSES = new Set<FieldExtractionStatus>([
    "complete",
    "failed",
    "not_applicable",
]);

// ---------------------------------------------------------------------------
// Extracted Properties
// ---------------------------------------------------------------------------

export interface IExtractedPropertyInDB {
    id: number;
    document_id: number;
    field_property_id: number;
    array_index: number;
    value: string | null;
    confidence: number | null;
    page_number: number | null;
    chunk_ids: number[] | null;
    is_edited: boolean;
    source: string | null;
    created_at: string;
    updated_at: string;
}

export interface IDocumentExtractedPropertyUpdate {
    value?: string | null;
    confidence?: number | null;
    page_number?: number | null;
    chunk_ids?: number[] | null;
}

export interface IExtractedPropertyValue {
    id: number;
    property_name: string;
    property_sort_order: number;
    value: string | null;
    raw_value?: string | null;
    confidence: number | null;
    page_number: number | null;
    is_edited: boolean;
    source: string | null;
}

// ---------------------------------------------------------------------------
// Custom Fields
// ---------------------------------------------------------------------------

export interface ICustomFieldPropertyResponse {
    id: number;
    name: string;
    value: string | null;
    page_number: number | null;
}

export interface IDocumentCustomFieldPropertyCreate {
    name: string;
    value?: string | null;
    page_number?: number | null;
}

export interface IDocumentCustomFieldCreate {
    name: string;
    policy_type_id: number;
    field_definition_id?: number | null;
    properties: IDocumentCustomFieldPropertyCreate[];
}

export interface ICustomFieldResponse {
    id: number;
    name: string;
    policy_type_id: number;
    field_definition_id: number | null;
    properties: ICustomFieldPropertyResponse[];
    created_at: string;
    updated_at: string;
}

// ---------------------------------------------------------------------------
// Extracted Fields (grouped response)
// ---------------------------------------------------------------------------

export interface IDocumentExtractedFieldItem {
    /**
     * Two different digests. `delete_fingerprint` guards an entity delete and
     * also covers cells of soft-deleted properties. `row_fingerprint` is the
     * correction token over active properties only, the digest reports
     * freeze; `null` means no stored row is here to correct. Absent on older
     * API revisions.
     */
    delete_fingerprint?: string;
    row_fingerprint?: string | null;
    array_index: number;
    display_name: string | null;
    properties: IExtractedPropertyValue[];
}

export interface IExtractedFieldResponse {
    field_definition_id: number;
    field_name: string;
    is_array: boolean;
    sort_order: number;
    items: IDocumentExtractedFieldItem[];
    /**
     * Singular label shown on each array-item badge when `display_name` is
     * unset (e.g., "Location" → badge "Location 1"). Sourced from the
     * fieldset definition; null when the schema doesn't pin a label, in
     * which case the UI falls back to "Item".
     */
    array_item_label?: string | null;
}

export interface IDocumentCustomFieldUpdate {
    name?: string;
}

export interface IDocumentFieldsByPolicyType {
    policy_type: string;
    policy_type_id: number;
    scalar_fields: IExtractedFieldResponse[];
    array_fields: IExtractedFieldResponse[];
    custom_fields: ICustomFieldResponse[];
}

export interface IDocumentExtractedFieldsResponse {
    document_id: number;
    field_extraction_status: FieldExtractionStatus;
    field_extraction_available: boolean;
    form_extraction_available: boolean;
    field_groups: IDocumentFieldsByPolicyType[];
}

// ---------------------------------------------------------------------------
// Highlight Areas (lazy per-property — kept off the polling response)
//
// Distinct from `IHighlightArea` in `scopes.types.ts`, which is tied to
// scope clauses. The wire shape is the same; the type name differs to
// avoid a barrel-export collision in `src/api/index.ts`.
// ---------------------------------------------------------------------------

export interface IPropertyHighlightArea {
    page_index: number;
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface IExtractedPropertyHighlightAreas {
    property_id: number;
    page_number: number | null;
    source: string | null;
    highlight_areas: IPropertyHighlightArea[] | null;
}

// ---------------------------------------------------------------------------
// Certificate of Insurance (COI)
// ---------------------------------------------------------------------------

export interface IExtractedCOIResponse {
    id: number;
    document_id: number;
    organization_id: number;
    page_num: number;
    certificate_number: string | null;
    certificate_date: string | null;
    revision_number: string | null;
    authorized_representative: string | null;
    producer_name: string | null;
    producer_address: string | null;
    producer_contact_name: string | null;
    producer_phone: string | null;
    producer_fax: string | null;
    producer_email: string | null;
    insured_name: string | null;
    insured_address: string | null;
    certificate_holder_name: string | null;
    certificate_holder_address: string | null;
    insurer_a_name: string | null;
    insurer_a_naic: string | null;
    insurer_b_name: string | null;
    insurer_b_naic: string | null;
    insurer_c_name: string | null;
    insurer_c_naic: string | null;
    insurer_d_name: string | null;
    insurer_d_naic: string | null;
    insurer_e_name: string | null;
    insurer_e_naic: string | null;
    insurer_f_name: string | null;
    insurer_f_naic: string | null;
    coverages: unknown[];
    description_of_operations: string | null;
    source: string | null;
    created_at: string;
    updated_at: string;
}

export type IExtractedCOIUpdate = Partial<
    Omit<
        IExtractedCOIResponse,
        | "id"
        | "document_id"
        | "organization_id"
        | "page_num"
        | "coverages"
        | "source"
        | "created_at"
        | "updated_at"
    >
>;

// ---------------------------------------------------------------------------
// Extraction Trigger
// ---------------------------------------------------------------------------

export interface IExtractionTriggerSkipped {
    pipeline: string;
    reason: string;
}

export interface IExtractionTriggerResponse {
    enqueued: string[];
    skipped: IExtractionTriggerSkipped[];
}

// ---------------------------------------------------------------------------
// Extraction Trigger Error Shapes
//
// FastAPI wraps HTTPException.detail in {"detail": <value>}, so the Axios
// response body always has a top-level `detail` key.
// ---------------------------------------------------------------------------

export interface IExtractionTriggerConflictResponse {
    detail: {
        running_or_queued: IExtractionTriggerSkipped[];
    };
}

export function isExtractionTriggerConflict(
    body: unknown
): body is IExtractionTriggerConflictResponse {
    if (typeof body !== "object" || body === null) return false;
    const detail = (body as Record<string, unknown>).detail;
    return (
        typeof detail === "object" &&
        detail !== null &&
        "running_or_queued" in detail &&
        Array.isArray((detail as Record<string, unknown>).running_or_queued)
    );
}

export function isExtractionUnavailableDetail(
    body: unknown
): body is { detail: string } {
    return (
        typeof body === "object" &&
        body !== null &&
        "detail" in body &&
        typeof (body as Record<string, unknown>).detail === "string"
    );
}

export interface IExtractionUnavailableResponse {
    detail: string;
    availability: {
        field_extraction_available: boolean;
        form_extraction_available: boolean;
    };
}

export function isExtractionUnavailableResponse(
    body: unknown
): body is IExtractionUnavailableResponse {
    if (typeof body !== "object" || body === null) return false;
    const record = body as Record<string, unknown>;
    if (typeof record.detail !== "string") return false;
    const availability = record.availability;
    if (typeof availability !== "object" || availability === null) return false;
    const a = availability as Record<string, unknown>;
    return (
        typeof a.field_extraction_available === "boolean" &&
        typeof a.form_extraction_available === "boolean"
    );
}
